<script setup>
import { defineAsyncComponent, onBeforeUnmount, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const emit = defineEmits(['select']);

const EmojiIconPicker = defineAsyncComponent(
  () => import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

// The shared Popover prefers opening downwards and only measures the picker
// after capping its height, so near the bottom of the screen it squeezed the
// picker into a strip. Here: lower half of the screen → open upwards.
const GAP = 8;
const EDGE = 8;
const PICKER_WIDTH = 352; // w-[22rem] of EmojiIconPicker

const isOpen = ref(false);
const position = ref({});
const triggerRef = ref(null);
const pickerRef = ref(null);

const close = () => {
  isOpen.value = false;
  window.removeEventListener('scroll', onScroll, true);
  window.removeEventListener('keydown', onKeydown);
};
// Scrolling the message list would leave the picker over the wrong message;
// scrolling inside the picker (its emoji list) is fine.
function onScroll(event) {
  if (!pickerRef.value?.contains(event.target)) close();
}
function onKeydown(event) {
  if (event.key === 'Escape') close();
}

const open = () => {
  const rect = triggerRef.value.getBoundingClientRect();
  const above = rect.top > window.innerHeight / 2;
  const left = Math.min(
    Math.max(EDGE, rect.right - PICKER_WIDTH),
    window.innerWidth - PICKER_WIDTH - EDGE
  );
  position.value = {
    left: `${Math.max(EDGE, left)}px`,
    ...(above
      ? {
          bottom: `${window.innerHeight - rect.top + GAP}px`,
          maxHeight: `${rect.top - GAP - EDGE}px`,
        }
      : {
          top: `${rect.bottom + GAP}px`,
          maxHeight: `${window.innerHeight - rect.bottom - GAP - EDGE}px`,
        }),
  };
  isOpen.value = true;
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('keydown', onKeydown);
};

const toggle = () => (isOpen.value ? close() : open());

const pick = emoji => {
  close();
  emit('select', emoji.value);
};

onBeforeUnmount(close);
</script>

<template>
  <span ref="triggerRef" class="inline-flex" @click="toggle">
    <slot />
  </span>
  <TeleportWithDirection>
    <div
      v-if="isOpen"
      ref="pickerRef"
      v-on-click-outside="[close, { ignore: [triggerRef] }]"
      class="fixed z-[9999] flex overflow-hidden rounded-2xl"
      :style="position"
    >
      <EmojiIconPicker mode="emoji" class="!static" @select="pick" />
    </div>
  </TeleportWithDirection>
</template>
